/**
 * Pure helpers for `@` mention detection inside a textarea value.
 *
 * A mention token starts with `@` that is either at the beginning of the text
 * or preceded by whitespace, and runs up to the caret without any whitespace.
 * `@` alone is a valid (empty) query, which opens the full member list.
 */

export type MentionQuery = {
  /** Index of the `@` character in the value. */
  start: number;
  /** Caret position (exclusive end of the query text). */
  end: number;
  /** Text typed after `@`, without the `@` itself. */
  query: string;
};

export function getMentionQuery(value: string, caret: number): MentionQuery | null {
  if (caret < 0 || caret > value.length) return null;
  const before = value.slice(0, caret);
  const at = before.lastIndexOf('@');
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(before[at - 1])) return null;
  const query = before.slice(at + 1);
  if (/\s/.test(query)) return null;
  return { start: at, end: caret, query };
}

/** Removes the `@query` token from the value and returns the new value + caret. */
export function removeMentionToken(
  value: string,
  mention: MentionQuery,
): { value: string; caret: number } {
  const head = value.slice(0, mention.start);
  const tail = value.slice(mention.end);
  // Drop the single space that preceded the `@` when the token was mid-text so
  // the surrounding words are not left with a double space.
  return { value: `${head}${tail}`, caret: head.length };
}

export type MentionCandidate = { id: string; name: string };

/** Case-insensitive "contains" filter on the display name, excluding already-selected ids. */
export function filterMentionUsers<T extends MentionCandidate>(
  users: readonly T[],
  query: string,
  selectedIds: readonly string[],
): T[] {
  const needle = query.trim().toLowerCase();
  const selected = new Set(selectedIds);
  return users.filter((user) => {
    if (selected.has(user.id)) return false;
    if (!needle) return true;
    return user.name.toLowerCase().includes(needle);
  });
}
